import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";
import { faqSchema, jsonLdScript } from "@/lib/seo-schema";

const POST = POSTS.find((p) => p.slug === "pdi-projected-digital-image-competitions");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/pdi-projected-digital-image-competitions";

// FAQPage JSON-LD extracted from the H2 Q&As on this page. Kept in sync
// with the visible copy so AI answer engines and Google SGE can cite
// verbatim without triggering a schema-mismatch penalty.
const FAQ_LD = faqSchema([
  {
    q: "What image size should members submit for PDI competitions?",
    a: "The most widely-adopted standard for UK camera clubs is 1600 pixels wide by 1200 pixels tall, sRGB, JPEG. This matches the native resolution of most club projectors and works well for federation battles. Some clubs go to 1920 × 1200 if their projector supports it.",
  },
  {
    q: "What colour space should digital images use for projection?",
    a: "sRGB, always. Anything else (Adobe RGB, ProPhoto) will look washed out or over-saturated on a projector, which uses sRGB regardless of what the image contains.",
  },
  {
    q: "How should PDI image files be named?",
    a: "Use the pattern PhotographerCode_ImageTitle.jpg — for example JS01_Winter_Lane.jpg. Underscores instead of spaces (some projection software chokes on spaces). Keep titles short so they don't overflow on the projected slide.",
  },
  {
    q: "How do I prepare images for projection?",
    a: "Three steps every experienced PDI secretary follows: (1) resize to the club's standard (e.g. 1600 × 1200), (2) convert to sRGB, (3) export as JPEG at quality 90–95%. Lightroom users should save a preset; Photoshop users should use Image → Automate → Batch. Script this or you'll spend an evening per competition doing it by hand.",
  },
  {
    q: "What's the best software for digital image competitions?",
    a: "For projection: dedicated tools like DiCentra or PicaJET project images in sequence and hide filenames from the audience. For scoring, results and season league tables: Camera Club Score. It doesn't project images itself (that's what your projection software is for) but handles everything after the score is called.",
  },
  {
    q: "How do we organise PDI competitions?",
    a: "Standard workflow: members submit files by email or through a shared drop-folder → PDI secretary batches into a running order → projection software queues them → live scoring in Camera Club Score → results are live before members leave the hall.",
  },
]);

export default function PostPDI() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
        <script type="application/ld+json">{jsonLdScript(FAQ_LD)}</script>
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          PDI (Projected Digital Image) competitions have quietly become
          the dominant format at UK camera clubs — cheaper for members,
          easier to store, faster to judge. But they come with their own
          set of gotchas. Here's the complete rundown.
        </p>

        <h2>What image size should members submit?</h2>
        <p>
          The most widely-adopted standard is <strong>1600 pixels wide by
          1200 pixels tall</strong>, sRGB, JPEG. This matches the native
          resolution of most club projectors and works well for federation
          battles. Some clubs go to <strong>1920 × 1200</strong> if their
          projector supports it.
        </p>

        <h2>What colour space should digital images use?</h2>
        <p>
          <strong>sRGB</strong>, always. Anything else (Adobe RGB, ProPhoto)
          will look washed out or over-saturated on a projector, which
          uses sRGB regardless of what the image contains.
        </p>

        <h2>How do I rename image files?</h2>
        <p>
          Naming conventions vary, but the safest pattern is:
        </p>
        <pre>PhotographerCode_ImageTitle.jpg</pre>
        <p>
          Example: <em>JS01_Winter_Lane.jpg</em>. Underscores instead of
          spaces (some projection software chokes on spaces). Keep titles
          short — long titles overflow on the projected slide.
        </p>

        <h2>How do I prepare images for projection?</h2>
        <p>
          The three-step recipe every experienced PDI secretary follows:
        </p>
        <ol>
          <li>Resize to the club's standard (e.g. 1600 × 1200).</li>
          <li>Convert to sRGB.</li>
          <li>Export as JPEG at quality 90–95%.</li>
        </ol>
        <p>
          Lightroom users: save a preset. Photoshop users: use Image →
          Automate → Batch. Get this scripted or you'll spend an evening
          per competition doing it by hand.
        </p>

        <h2>What's the best software for digital image competitions?</h2>
        <p>
          For <strong>projection</strong>: dedicated tools like{" "}
          <em>DiCentra</em> or <em>PicaJET</em> project images in sequence
          and hide filenames from the audience.
        </p>
        <p>
          For <strong>scoring, results, and season league tables</strong>:{" "}
          <Link to="/">Camera Club Score</Link> — it doesn't project images
          itself (that's what your projection software is for), but it
          handles everything after the score is called.
        </p>

        <h2>How do we organise PDI competitions?</h2>
        <p>
          Standard workflow: members submit files by email or through a
          shared drop-folder → PDI secretary batches into a running order →
          projection software queues them → live scoring in Camera Club
          Score → results are live before members leave the hall.
        </p>

        <p>
          <Link to="/register">Try Camera Club Score free for 30 days</Link>{" "}
          and see how it fits your next PDI round.
        </p>
      </BlogPostShell>
    </>
  );
}
